import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../api';
import { formatCount } from '../utils/dates';
import { getThumbnailUrl, getAvatarUrl } from '../utils/media';
import { useThumbnailAuth } from '../hooks/useThumbnailAuth';
import { useAvatarAuth } from '../hooks/useAvatarAuth';

const CATEGORY_OPTIONS = [
  '', 'Cinema', 'Series', 'Documentaries', 'Courses',
  'Mentorships', 'Animation', 'Music', 'Other',
];

const FONT_OPTIONS = [
  { value: '', label: 'Nunito (default)' },
  { value: 'Playfair Display', label: 'Playfair Display' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Oswald', label: 'Oswald' },
  { value: 'Lora', label: 'Lora' },
  { value: 'Space Grotesk', label: 'Space Grotesk' },
  { value: 'Bebas Neue', label: 'Bebas Neue' },
  { value: 'Cormorant Garamond', label: 'Cormorant Garamond' },
  { value: 'Raleway', label: 'Raleway' },
  { value: 'Crimson Text', label: 'Crimson Text' },
  { value: 'Archivo Black', label: 'Archivo Black' },
  { value: 'DM Serif Display', label: 'DM Serif Display' },
  { value: 'Outfit', label: 'Outfit' },
  { value: 'Syne', label: 'Syne' },
  // Cursive
  { value: 'Dancing Script', label: 'Dancing Script' },
  { value: 'Great Vibes', label: 'Great Vibes' },
  { value: 'Pacifico', label: 'Pacifico' },
  { value: 'Sacramento', label: 'Sacramento' },
  { value: 'Satisfy', label: 'Satisfy' },
  // Display
  { value: 'Permanent Marker', label: 'Permanent Marker' },
  { value: 'Righteous', label: 'Righteous' },
  { value: 'Bungee', label: 'Bungee' },
  { value: 'Monoton', label: 'Monoton' },
  { value: 'Press Start 2P', label: 'Press Start 2P' },
  { value: 'Creepster', label: 'Creepster' },
];

const COLOR_PRESETS = [
  '', '#C8963E', '#5B8C7A', '#8B4513', '#1a1a1a',
  '#2D1B69', '#1B3A4B', '#4A1A2E', '#2F4F2F', '#3B2F2F',
];

const GOOGLE_FONTS_TO_LOAD = [
  'Playfair Display', 'Montserrat', 'Lora', 'Space Grotesk',
  'Bebas Neue', 'Cormorant Garamond', 'Raleway', 'Crimson Text',
  'Archivo Black', 'DM Serif Display', 'Outfit', 'Syne',
  'Dancing Script', 'Great Vibes', 'Pacifico', 'Sacramento', 'Satisfy',
  'Permanent Marker', 'Righteous', 'Bungee', 'Monoton', 'Press Start 2P', 'Creepster',
];

export default function CreatorProfile() {
  const { username } = useParams();
  const { user: authUser } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const thumbAuth = useThumbnailAuth();
  const avatarAuth = useAvatarAuth();
  const fileInputRef = useRef(null);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscribed, setSubscribed] = useState(false);
  const [subCount, setSubCount] = useState(0);
  const [copyToast, setCopyToast] = useState(false);

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ displayName: '', bio: '', contentCategory: '', profileFont: '', profileBioFont: '', profileBgColor: '' });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  // Load Google Fonts
  useEffect(() => {
    const families = GOOGLE_FONTS_TO_LOAD.map(f => f.replace(/ /g, '+')).join('&family=');
    const id = 'cp-google-fonts';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${families}&display=swap`;
      document.head.appendChild(link);
    }
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/creator/${username}`);
      setProfile(data);
      setSubscribed(!!data.isSubscribed);
      setSubCount(data.stats?.subscribers || 0);
      setEditForm({
        displayName: data.display_name || '',
        bio: data.bio || '',
        contentCategory: data.content_category || '',
        profileFont: data.profile_font || '',
        profileBioFont: data.profile_bio_font || '',
        profileBgColor: data.profile_bg_color || '',
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Creator not found');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [username]);

  const isOwner = authUser && profile && Number(authUser.id) === Number(profile.id);
  const isCreator = profile?.role === 'creator' || profile?.contents?.length > 0;

  // Compute bg color early so the useEffect can run before early returns
  const activeBgColor = isCreator
    ? (editing ? editForm.profileBgColor : (profile?.profile_bg_color || ''))
    : '';

  // Apply bg color to entire body
  useEffect(() => {
    if (activeBgColor) {
      document.body.style.background = `linear-gradient(135deg, ${activeBgColor} 0%, #0d0d0d 60%, ${activeBgColor}CC 100%)`;
      document.body.style.backgroundAttachment = 'fixed';
    } else {
      document.body.style.background = '';
      document.body.style.backgroundAttachment = '';
    }
    return () => {
      document.body.style.background = '';
      document.body.style.backgroundAttachment = '';
    };
  }, [activeBgColor]);

  const handleShare = () => {
    const url = `${window.location.origin}/profile/${profile.username}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopyToast(true);
      setTimeout(() => setCopyToast(false), 2000);
    });
  };

  const handleEditStart = () => {
    setEditing(true);
    setEditError(null);
    setAvatarFile(null);
    setAvatarPreview(null);
  };

  const handleEditCancel = () => {
    setEditing(false);
    setEditError(null);
    setAvatarFile(null);
    setAvatarPreview(null);
    setEditForm({
      displayName: profile.display_name || '',
      bio: profile.bio || '',
      contentCategory: profile.content_category || '',
      profileFont: profile.profile_font || '',
      profileBioFont: profile.profile_bio_font || '',
      profileBgColor: profile.profile_bg_color || '',
    });
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setEditError('Image must be under 2MB');
      return;
    }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setEditError(null);

    // Auto-save avatar immediately
    try {
      const formData = new FormData();
      formData.append('username', profile.username);
      formData.append('avatar', file);
      await api.put('/creator/profile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchProfile();
      setAvatarFile(null);
      setAvatarPreview(null);
    } catch (err) {
      setEditError(err.response?.data?.error || 'Error uploading avatar');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setEditError(null);
    setSaving(true);

    try {
      const formData = new FormData();
      formData.append('username', profile.username);
      formData.append('displayName', editForm.displayName);
      formData.append('bio', editForm.bio);
      formData.append('contentCategory', editForm.contentCategory);
      formData.append('profileFont', editForm.profileFont);
      formData.append('profileBioFont', editForm.profileBioFont);
      formData.append('profileBgColor', editForm.profileBgColor);
      if (avatarFile) {
        formData.append('avatar', avatarFile);
      }

      await api.put('/creator/profile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      await fetchProfile();
      setEditing(false);
      setAvatarFile(null);
      setAvatarPreview(null);
    } catch (err) {
      setEditError(err.response?.data?.error || 'Error saving profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSubscribe = async () => {
    if (!authUser) { navigate('/login'); return; }
    try {
      const { data } = await api.post(`/creator/${username}/subscribe`);
      setSubscribed(data.subscribed);
      setSubCount(data.subscriberCount);
    } catch (err) {
      // silently ignore self-subscribe error
    }
  };

  if (loading) return <div className="loading">Loading profile...</div>;
  if (error) return <div className="empty">{error}</div>;
  if (!profile) return null;

  const initial = (profile.display_name || profile.username || '?')[0].toUpperCase();
  const avatarSrc = avatarPreview || getAvatarUrl(profile.avatar_url, avatarAuth);
  const stats = profile.stats || { videos: 0, views: 0, likes: 0 };

  // Determine active font (preview from editForm when editing, otherwise from profile)
  const activeFont = editing ? editForm.profileFont : (profile.profile_font || '');
  const activeBioFont = editing ? editForm.profileBioFont : (profile.profile_bio_font || '');
  const titleFontFamily = activeFont || 'Nunito, sans-serif';
  const bioFontFamily = activeBioFont || 'Nunito, sans-serif';

  // ── Normal user (non-creator) basic profile ──
  if (!isCreator) {
    return (
      <div className="cp-page">
        <div className="cp-basic-card">
          <div
            className={`cp-avatar${isOwner ? ' cp-avatar-editable' : ''}`}
            onClick={isOwner ? () => fileInputRef.current?.click() : undefined}
          >
            {avatarSrc ? (
              <img src={avatarSrc} alt={profile.display_name} />
            ) : (
              <span>{initial}</span>
            )}
            {isOwner && <div className="cp-avatar-overlay">Edit</div>}
          </div>
          {isOwner && (
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleAvatarChange}
              style={{ display: 'none' }}
            />
          )}
          <h1 className="cp-basic-name">{profile.display_name}</h1>
          <p className="cp-basic-username">@{profile.username}</p>
          {profile.bio && !editing && <p className="cp-basic-bio">{profile.bio}</p>}
          {isOwner && (
            <button className={`btn ${editing ? 'btn-primary' : 'btn-outline'}`} onClick={editing ? handleEditCancel : handleEditStart} style={{ marginTop: 16 }}>
              {editing ? 'Cancel' : 'Edit Profile'}
            </button>
          )}
          {editing && isOwner && (
            <form className="cp-edit-form" onSubmit={handleSave} style={{ marginTop: 16 }}>
              <label className="cp-edit-label">
                Name
                <input
                  type="text"
                  value={editForm.displayName}
                  onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                  placeholder="Your name"
                  maxLength={50}
                />
              </label>
              <label className="cp-edit-label">
                Bio
                <textarea
                  value={editForm.bio}
                  onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                  placeholder="Tell the world about yourself..."
                  rows={3}
                  maxLength={500}
                />
              </label>
              {editError && <div className="error-msg">{editError}</div>}
              <div className="cp-edit-buttons">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button type="button" className="btn btn-outline" onClick={handleEditCancel}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  // ── Creator cinematic profile ──
  const glowColor = activeBgColor || '#C8963E';

  return (
    <div className="cp-page">
      {/* ── Cinematic Hero ── */}
      <div className="cp-hero">
        <div className="cp-hero-inner">
          <div
            className={`cp-avatar${isOwner ? ' cp-avatar-editable' : ''}`}
            style={{ boxShadow: `0 0 20px ${glowColor}55, 0 0 40px ${glowColor}22` }}
            onClick={isOwner ? () => fileInputRef.current?.click() : undefined}
          >
            {avatarSrc ? (
              <img src={avatarSrc} alt={profile.display_name} />
            ) : (
              <span>{initial}</span>
            )}
            {isOwner && <div className="cp-avatar-overlay">Edit</div>}
          </div>
          {isOwner && (
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleAvatarChange}
              style={{ display: 'none' }}
            />
          )}

          <p className="cp-username">@{profile.username}</p>
          <div className="cp-info">
            <h1 className="cp-display-name" style={{ fontFamily: titleFontFamily }}>{profile.display_name}</h1>
            {(profile.content_category && !editing) && (
              <span className="cp-category-badge">{profile.content_category}</span>
            )}
          </div>
          {profile.bio && !editing && (
            <p className="cp-bio" style={{ fontFamily: bioFontFamily }}>{profile.bio}</p>
          )}
        </div>
      </div>

      {/* ── Actions Bar ── */}
      <div className="cp-actions">
        <button className="cp-share-btn" onClick={handleShare}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
            <polyline points="16 6 12 2 8 6" />
            <line x1="12" y1="2" x2="12" y2="15" />
          </svg>
          Share
        </button>
        {isOwner ? (
          <button className={`btn ${editing ? 'btn-primary' : 'btn-outline'} cp-action-btn`} onClick={editing ? handleEditCancel : handleEditStart}>
            {editing ? 'Cancel' : 'Edit Profile'}
          </button>
        ) : (
          <button className={`cp-subscribe-btn${subscribed ? ' cp-subscribed' : ''}`} onClick={handleSubscribe}>
            {subscribed ? 'Subscribed' : 'Subscribe'}
          </button>
        )}
        <div className="cp-stats">
          <div className="cp-stat">
            <span className="cp-stat-count">{formatCount(stats.videos)}</span>
            <span className="cp-stat-label">Videos</span>
          </div>
          <div className="cp-stat">
            <span className="cp-stat-count">{formatCount(subCount)}</span>
            <span className="cp-stat-label">Subscribers</span>
          </div>
          <div className="cp-stat">
            <span className="cp-stat-count">{formatCount(stats.views)}</span>
            <span className="cp-stat-label">Views</span>
          </div>
          <div className="cp-stat">
            <span className="cp-stat-count">{formatCount(stats.likes)}</span>
            <span className="cp-stat-label">Likes</span>
          </div>
        </div>
      </div>

      {/* ── Edit Panel (owner only) ── */}
      {editing && isOwner && (
        <form className="cp-edit-form" onSubmit={handleSave}>
          {/* Display name */}
          <label className="cp-edit-label">
            Name
            <input
              type="text"
              value={editForm.displayName}
              onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
              placeholder="Your name or brand"
              maxLength={50}
            />
          </label>

          {/* Title font picker */}
          <label className="cp-edit-label">
            Title font
            <select
              value={editForm.profileFont}
              onChange={(e) => setEditForm({ ...editForm, profileFont: e.target.value })}
            >
              {FONT_OPTIONS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </label>
          <div className="cp-font-preview" style={{ fontFamily: editForm.profileFont || 'Nunito, sans-serif' }}>
            Title preview
          </div>

          {/* Bio font picker */}
          <label className="cp-edit-label">
            Bio font
            <select
              value={editForm.profileBioFont}
              onChange={(e) => setEditForm({ ...editForm, profileBioFont: e.target.value })}
            >
              {FONT_OPTIONS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </label>
          <div className="cp-font-preview" style={{ fontFamily: editForm.profileBioFont || 'Nunito, sans-serif' }}>
            Bio preview
          </div>

          {/* Color picker */}
          <label className="cp-edit-label">
            Background color
          </label>
          <div className="cp-color-picker">
            <div className="cp-color-swatches">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c || 'none'}
                  type="button"
                  className={`cp-color-swatch${editForm.profileBgColor === c ? ' cp-color-active' : ''}`}
                  style={{ background: c || '#0d0d0d', border: c ? 'none' : '2px dashed #555' }}
                  onClick={() => setEditForm({ ...editForm, profileBgColor: c })}
                  title={c || 'No color'}
                />
              ))}
            </div>
            <input
              type="text"
              className="cp-color-input"
              value={editForm.profileBgColor}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || /^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                  setEditForm({ ...editForm, profileBgColor: val });
                }
              }}
              placeholder="#RRGGBB"
              maxLength={7}
            />
          </div>

          {/* Bio */}
          <label className="cp-edit-label">
            Bio
            <textarea
              value={editForm.bio}
              onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
              placeholder="Tell the world about your content..."
              rows={3}
              maxLength={500}
            />
          </label>

          {/* Category */}
          <label className="cp-edit-label">
            Category/Subcategory
            <select
              value={editForm.contentCategory}
              onChange={(e) => setEditForm({ ...editForm, contentCategory: e.target.value })}
            >
              {CATEGORY_OPTIONS.map((cat) => (
                <option key={cat} value={cat}>
                  {cat || '— No category —'}
                </option>
              ))}
            </select>
          </label>

          {editError && <div className="error-msg">{editError}</div>}
          <div className="cp-edit-buttons">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" className="btn btn-outline" onClick={handleEditCancel}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* ── Video Grid ── */}
      <div className="cp-grid-section">
        {profile.contents.length === 0 ? (
          <p className="cp-empty">No published content yet.</p>
        ) : (
          <div className="cp-editorial">
            {/* Featured — first video */}
            {(() => {
              const c = profile.contents[0];
              const thumb = getThumbnailUrl(c.media_url, c.thumbnail_key, thumbAuth);
              return (
                <Link to={`/content/${c.id}`} className="cp-ed-card">
                  <div className="cp-ed-thumb">
                    {thumb ? (
                      <img src={thumb} alt={c.title} loading="lazy" />
                    ) : (
                      <div className="cp-ed-placeholder">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <polygon points="5,3 19,12 5,21" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="cp-ed-info">
                    <h3>{c.title}</h3>
                    {c.description && <p className="cp-ed-desc">{c.description}</p>}
                    <div className="cp-ed-meta">
                      <span>{formatCount(c.view_count || 0)} views</span>
                      <span className="cp-ed-price">{parseFloat(parseFloat(c.price_usdc).toFixed(2))} $</span>
                    </div>
                  </div>
                </Link>
              );
            })()}

            {/* Rest — same grid as home */}
            {profile.contents.length > 1 && (
              <div className="content-grid">
                {profile.contents.slice(1).map((c) => {
                  const thumb = getThumbnailUrl(c.media_url, c.thumbnail_key, thumbAuth);
                  return (
                    <Link to={`/content/${c.id}`} key={c.id} className="content-card" style={{ textDecoration: 'none', color: 'inherit' }}>
                      <div className="content-card-thumb">
                        {thumb ? (
                          <img src={thumb} alt={c.title} loading="lazy" />
                        ) : (
                          <div className="content-card-placeholder">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <polygon points="5,3 19,12 5,21" />
                            </svg>
                            <span>No thumbnail</span>
                          </div>
                        )}
                      </div>
                      <div className="content-card-info">
                        <h3>{c.title}</h3>
                        <div className="content-card-meta">
                          <span className="content-card-creator">{formatCount(c.view_count || 0)} views</span>
                          <span className="content-card-price">{parseFloat(parseFloat(c.price_usdc).toFixed(2))} $</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Copy Toast ── */}
      {copyToast && (
        <div className="cp-copy-toast">Link copied!</div>
      )}
    </div>
  );
}
